'use client'

import { useEffect, useRef, useState, useTransition } from 'react'
import Link from 'next/link'
import { type FulfillmentCity } from '@entities/fulfillment'
import { Button } from '@ui/base/button'
import { Field, FieldDescription, FieldLabel } from '@ui/base/field'
import { Input } from '@ui/base/input'
import { Popover, PopoverContent, PopoverPositioner, PopoverTrigger } from '@ui/base/popover'

import { type CatalogSearchParams } from '../lib/search-params'
import { isPlainClick, useCatalogFilters } from './catalog-filters-context'

type Props = {
  params: CatalogSearchParams
  cities: FulfillmentCity[]
}

export const CatalogCityChooser = (props: Props) => (
  <CatalogCityChooserState key={props.params.cityQuery ?? ''} {...props} />
)

const CatalogCityChooserState = ({ params, cities }: Props) => {
  const { navigate, linkProps } = useCatalogFilters()
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<number | null>(null)
  const suppressDebounceRef = useRef(false)
  const [open, setOpen] = useState(Boolean(params.cityQuery))
  const [query, setQuery] = useState(params.cityQuery ?? '')
  const [, startTransition] = useTransition()

  useEffect(() => {
    if (open) {
      inputRef.current?.focus()
    }
  }, [open])

  useEffect(() => {
    if (suppressDebounceRef.current) {
      suppressDebounceRef.current = false
      return
    }
    if (query === (params.cityQuery ?? '')) {
      return
    }
    debounceRef.current = window.setTimeout(() => {
      startTransition(() => {
        // Замена записи в истории: иначе кнопка «назад» отматывала бы поиск по букве.
        navigate(() => ({ cityQuery: query || undefined, page: undefined }), { replace: true })
      })
    }, 250)
    return () => {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
    }
  }, [params, query, navigate])

  /**
   * Город выбран: отложенный поиск гасим, строку чистим, окно закрываем. Флаг нужен, чтобы
   * очистка строки сама не запустила ещё один поиск.
   */
  const closeAfterChoice = () => {
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    suppressDebounceRef.current = true
    setQuery('')
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger render={<Button type="button" size="sm" variant="ghost" className="justify-start" />}>
        Другой город
      </PopoverTrigger>
      <PopoverPositioner align="end">
        <PopoverContent className="w-[min(24rem,calc(100vw-2rem))]">
          <div role="search" aria-label="Поиск другого города">
            <Field>
              <FieldLabel htmlFor="catalog-city-query">Найти город</FieldLabel>
              <Input
                ref={inputRef}
                id="catalog-city-query"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                maxLength={160}
                placeholder="Начните вводить название"
              />
            </Field>
            {params.cityQuery && (
              <div className="mt-3 flex flex-wrap gap-2">
                {cities.length ? (
                  cities.map((city) => {
                    const { href, onClick } = linkProps(() => ({
                      city: city.id,
                      cityQuery: undefined,
                      page: undefined,
                    }))

                    return (
                      <Button
                        key={city.id}
                        variant="outline"
                        render={
                          <Link
                            href={href}
                            aria-current={params.city === city.id ? 'page' : undefined}
                            onClick={(event) => {
                              // Перехваченный клик уводит на новый адрес — окно поиска больше не нужно.
                              if (isPlainClick(event)) {
                                closeAfterChoice()
                              }
                              onClick(event)
                            }}
                          />
                        }
                      >
                        {city.name}
                      </Button>
                    )
                  })
                ) : (
                  <FieldDescription>Город не найден</FieldDescription>
                )}
              </div>
            )}
          </div>
        </PopoverContent>
      </PopoverPositioner>
    </Popover>
  )
}
